import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dah84k3oh653ivfvnnog';

export default function SubmissionForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('company_alt') ?? '').trim();
    if (honeypot) {
      formEl.reset();
      setStatus('success');
      setMessage('投稿已收到，我们会尽快整理核实。');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'company_alt') return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        formEl.reset();
        setStatus('success');
        setMessage('投稿已收到，我们会尽快整理核实。');
      } else {
        setStatus('error');
        setMessage(
          isSpam ? '这条投稿未能通过校验，请调整内容后重试。' : `提交失败：${serverMsg || '请稍后重试'}`
        );
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，投稿没有提交成功，请稍后重试。');
    }
  };

  return (
    <form
      id="submission-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6"
    >
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
        <i className="ri-quill-pen-line text-primary-500" aria-hidden="true" />
        我要投稿一条瓜
      </h3>
      <p className="mt-2 text-xs leading-relaxed text-foreground-500">
        请尽量写清时间、地点和你能公开查到的材料来源。不要提交他人隐私信息，也不要提交未经证实的指控。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="submission-nickname" className="mb-1.5 block text-xs font-medium text-foreground-700">
            昵称
          </label>
          <input
            id="submission-nickname"
            name="nickname"
            type="text"
            required
            maxLength={20}
            placeholder="用于显示投稿人"
            className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="submission-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
            邮箱（用于核实与回复）
          </label>
          <input
            id="submission-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="submission-title" className="mb-1.5 block text-xs font-medium text-foreground-700">
          事件标题
        </label>
        <input
          id="submission-title"
          name="title"
          type="text"
          required
          maxLength={60}
          placeholder="一句话概括，例如「某剧组餐标争议」"
          className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="submission-category" className="mb-1.5 block text-xs font-medium text-foreground-700">
            所属分类
          </label>
          <select
            id="submission-category"
            name="category"
            defaultValue="romance"
            className="h-11 w-full cursor-pointer rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 focus:border-primary-300 focus:outline-none"
          >
            <option value="romance">恋情绯闻</option>
            <option value="backstage">影视幕后</option>
            <option value="variety">综艺现场</option>
            <option value="music">音乐动态</option>
            <option value="idol">选秀爱豆</option>
            <option value="redcarpet">红毯活动</option>
          </select>
        </div>
        <div>
          <label htmlFor="submission-credibility" className="mb-1.5 block text-xs font-medium text-foreground-700">
            你自评的可信度
          </label>
          <select
            id="submission-credibility"
            name="credibility"
            defaultValue="medium"
            className="h-11 w-full cursor-pointer rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 focus:border-primary-300 focus:outline-none"
          >
            <option value="high">较高 · 有我见过的材料</option>
            <option value="medium">中等 · 需要进一步核实</option>
            <option value="low">较低 · 听说的，仅供参考</option>
          </select>
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="submission-detail" className="mb-1.5 block text-xs font-medium text-foreground-700">
          事件经过（500 字以内）
        </label>
        <textarea
          id="submission-detail"
          name="detail"
          required
          maxLength={500}
          rows={5}
          placeholder="时间、地点、发生了什么，以及目前流传的说法有哪些…"
          className="w-full resize-y rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
        />
      </div>

      <div className="mt-4">
        <label htmlFor="submission-source" className="mb-1.5 block text-xs font-medium text-foreground-700">
          材料来源（选填）
        </label>
        <input
          id="submission-source"
          name="source"
          type="text"
          maxLength={120}
          placeholder="公开可查的链接或说明，例如「某平台官方账号发布的视频」"
          className="h-11 w-full rounded-md border border-background-200 bg-background-100 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
        />
      </div>

      <div className="form-honeypot-guard" aria-hidden="true">
        <label htmlFor="submission-company-alt">公司名称（选填）</label>
        <input
          id="submission-company-alt"
          name="company_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
          aria-hidden="true"
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[11px] leading-relaxed text-foreground-400">
          提交后我们会人工核实，若采纳会标注为「网友投稿」。
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} aria-hidden="true" />
          {status === 'submitting' ? '提交中…' : '提交投稿'}
        </button>
      </div>

      {status === 'success' && (
        <p className="mt-3 flex items-center gap-1.5 rounded-md bg-secondary-100 px-3 py-2 text-xs text-secondary-900">
          <i className="ri-checkbox-circle-line" aria-hidden="true" />
          {message}
        </p>
      )}
      {status === 'error' && (
        <p className="mt-3 flex items-center gap-1.5 rounded-md bg-primary-100 px-3 py-2 text-xs text-primary-800">
          <i className="ri-error-warning-line" aria-hidden="true" />
          {message}
        </p>
      )}
    </form>
  );
}